import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Calendar, Film, RotateCw, Send, Loader2, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function formatKickoff(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-ES', {
      weekday: 'short', day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid',
    });
  } catch { return iso; }
}

function tomorrowISO() {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

const PALETTE_HINT = {
  laliga_red_gold:        'border-red-700/40 bg-gradient-to-br from-red-950/60 to-amber-950/30',
  serie_a_tricolore:      'border-emerald-700/40 bg-gradient-to-br from-emerald-950/60 to-red-950/30',
  premier_purple_lime:    'border-violet-700/40 bg-gradient-to-br from-violet-950/60 to-lime-950/20',
  bundesliga_red_yellow:  'border-amber-600/40 bg-gradient-to-br from-red-950/60 to-amber-900/30',
  ucl_navy_silver:        'border-sky-700/40 bg-gradient-to-br from-slate-950/80 to-zinc-800/40',
};

function VideoBlob({ url }) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let revoked = null;
    if (!url) { setBlobUrl(null); return; }
    fetch(`${API_URL}${url}`, { headers: authHeader() })
      .then((r) => r.blob())
      .then((b) => {
        const u = URL.createObjectURL(b);
        revoked = u;
        setBlobUrl(u);
      })
      .catch(() => setBlobUrl(null));
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [url]);
  if (!url) return null;
  if (!blobUrl) {
    return (
      <div className="aspect-[9/16] bg-zinc-950/80 rounded-md flex items-center justify-center text-zinc-500">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  return (
    <video
      src={blobUrl}
      controls
      muted
      playsInline
      className="aspect-[9/16] w-full rounded-md bg-black object-contain"
      data-testid={`announce-video-${url.split('/').pop()}`}
    />
  );
}

function LeagueCard({ item, onAfterAction }) {
  const { league_id, league_label, base_template, palette, fixture, media_url, last_render_at, publication } = item;
  const [rendering, setRendering] = useState(false);
  const [republishing, setRepublishing] = useState(false);
  const hasFixture = !!fixture;

  const doRender = useCallback(async () => {
    if (!fixture) return;
    setRendering(true);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/match-announce/render/${fixture.id}?league_id=${league_id}&channel=free`,
        { method: 'POST', headers: authHeader() },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      toast.success(`Render OK · ${d.league_key} · ${d.round_label || ''}`);
      onAfterAction?.();
    } catch (e) {
      toast.error('Falló render: ' + e.message);
    } finally {
      setRendering(false);
    }
  }, [fixture, league_id, onAfterAction]);

  const doRepublish = useCallback(async () => {
    if (!fixture) return;
    setRepublishing(true);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/match-announce/republish/${fixture.id}?league_id=${league_id}&channel=free`,
        { method: 'POST', headers: authHeader() },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      toast.success(`Republish encolado · pub_id=${d.publication_id?.slice(0, 8)}…`);
      onAfterAction?.();
    } catch (e) {
      toast.error('Falló republish: ' + e.message);
    } finally {
      setRepublishing(false);
    }
  }, [fixture, league_id, onAfterAction]);

  return (
    <Card className={`${PALETTE_HINT[palette] || 'border-zinc-800 bg-zinc-900/40'} overflow-hidden`}
          data-testid={`announce-card-${item.league_key}`}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-white text-sm">
          <span className="font-black tracking-wide">{league_label}</span>
          <Badge className="bg-zinc-900/70 text-zinc-300 border border-zinc-700 text-[10px]">
            {base_template.toUpperCase()}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!hasFixture && (
          <div className="text-xs text-zinc-500 py-6 text-center">
            Sin partido programado para esta fecha
          </div>
        )}
        {hasFixture && (
          <>
            <div className="text-sm">
              <div className="text-white font-semibold leading-tight">
                {fixture.home_team} <span className="text-zinc-500">vs</span> {fixture.away_team}
              </div>
              <div className="flex items-center gap-1.5 text-xs text-zinc-400 mt-1">
                <Calendar className="w-3 h-3" />
                {formatKickoff(fixture.match_date)}
              </div>
              {(fixture.venue || fixture.city) && (
                <div className="text-[10px] text-zinc-500 mt-0.5 truncate">
                  {[fixture.venue, fixture.city].filter(Boolean).join(' · ')}
                </div>
              )}
              {fixture.fixtures_in_league > 1 && (
                <div className="text-[10px] text-amber-400/80 mt-1">
                  +{fixture.fixtures_in_league - 1} más en la liga (mostramos el más relevante)
                </div>
              )}
            </div>

            {media_url ? (
              <VideoBlob url={media_url} />
            ) : (
              <div className="aspect-[9/16] bg-zinc-950/80 rounded-md flex flex-col items-center justify-center text-zinc-500 text-xs gap-2 border border-dashed border-zinc-800">
                <ImageIcon className="w-6 h-6 text-zinc-700" />
                Sin render todavía
              </div>
            )}

            {publication && (
              <div className="text-[11px] flex flex-col gap-1">
                <Badge className={
                  publication.status === 'published' ? 'bg-emerald-900/40 text-emerald-300 border-emerald-700/40 self-start'
                  : publication.status === 'pending' ? 'bg-amber-900/30 text-amber-300 border-amber-700/40 self-start'
                  : 'bg-red-900/40 text-red-300 border-red-700/40 self-start'
                }>
                  {publication.status} · {publication.channel_target}
                </Badge>
                {publication.hold_reason && (
                  <span className="text-amber-400/80 text-[10px]">⏸ {publication.hold_reason}</span>
                )}
                {publication.error && publication.status === 'failed' && (
                  <span className="text-red-400/80 text-[10px] truncate" title={publication.error}>{publication.error}</span>
                )}
              </div>
            )}

            {last_render_at && (
              <div className="text-[10px] text-zinc-500">
                Último render: {new Date(last_render_at).toLocaleString('es-ES')}
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <Button
                size="sm"
                onClick={doRender}
                disabled={rendering}
                data-testid={`announce-render-${item.league_key}`}
                className="flex-1 bg-sky-700 hover:bg-sky-600 text-white"
              >
                {rendering ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Film className="w-3.5 h-3.5" />}
                <span className="ml-1">{media_url ? 'Re-render' : 'Render'}</span>
              </Button>
              <Button
                size="sm"
                onClick={doRepublish}
                disabled={republishing || !media_url}
                data-testid={`announce-republish-${item.league_key}`}
                variant="outline"
                className="flex-1 border-violet-700 text-violet-200 hover:bg-violet-950/40"
                title={!media_url ? 'Renderiza primero' : 'Re-encolar publicación'}
              >
                {republishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                <span className="ml-1">Republish</span>
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function MatchAnnounceGallery() {
  const [date, setDate] = useState(tomorrowISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/match-announce/gallery?date=${date}`,
                            { headers: authHeader() });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const d = await r.json();
      setData(d);
    } catch (e) {
      toast.error('No se pudo cargar gallery: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { refresh(); }, [refresh]);

  const renderAll = useCallback(async () => {
    if (!data) return;
    const candidates = data.leagues.filter((l) => l.fixture && !l.media_url);
    if (candidates.length === 0) {
      toast.info('Todos los partidos ya están renderizados');
      return;
    }
    toast.info(`Renderizando ${candidates.length} cards…`);
    for (const l of candidates) {
      try {
        await fetch(
          `${API_URL}/api/admin/match-announce/render/${l.fixture.id}?league_id=${l.league_id}&channel=free`,
          { method: 'POST', headers: authHeader() },
        );
      } catch { /* keep going */ }
    }
    await refresh();
    toast.success('Render batch completado');
  }, [data, refresh]);

  const counts = useMemo(() => {
    if (!data) return { total: 0, with_fx: 0, rendered: 0, queued: 0 };
    const ls = data.leagues || [];
    return {
      total: ls.length,
      with_fx: ls.filter((l) => l.fixture).length,
      rendered: ls.filter((l) => l.media_url).length,
      queued: ls.filter((l) => l.publication && l.publication.status === 'pending').length,
    };
  }, [data]);

  return (
    <Card className="bg-zinc-900/60 border-emerald-700/30 mb-4" data-testid="match-announce-gallery">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-white text-base">
          <div className="flex items-center gap-2">
            <Film className="w-4 h-4 text-emerald-300" />
            <span>Match Announces · vista previa</span>
            <Badge className="bg-zinc-800 text-zinc-400 border-zinc-700 text-[10px]">
              {counts.with_fx}/{counts.total} con partido · {counts.rendered} render · {counts.queued} encoladas
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="announce-gallery-date"
              className="bg-zinc-950 border border-zinc-700 rounded text-zinc-200 text-xs px-2 py-1"
            />
            <Button size="sm" variant="outline" onClick={refresh} disabled={loading}
                    data-testid="announce-gallery-refresh"
                    className="border-zinc-700 hover:bg-zinc-800">
              <RotateCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button size="sm" onClick={renderAll} disabled={loading || !data}
                    data-testid="announce-gallery-render-all"
                    className="bg-emerald-700 hover:bg-emerald-600 text-white">
              <Film className="w-3.5 h-3.5 mr-1" /> Render todos
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading && !data ? (
          <div className="flex items-center justify-center py-10 text-zinc-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {(data?.leagues || []).map((l) => (
              <LeagueCard key={l.league_id} item={l} onAfterAction={refresh} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default MatchAnnounceGallery;
